import api from './api';
import { API_ENDPOINTS } from '../constants';

export interface AttendanceRecord {
  id: string;
  user_id: string;
  store_id?: string;
  cluster_id?: string;
  date: string;
  clock_in_time?: string;
  clock_out_time?: string;
  clock_in_latitude?: string;
  clock_in_longitude?: string;
  clock_out_latitude?: string;
  clock_out_longitude?: string;
  clock_in_photo_url?: string;
  clock_out_photo_url?: string;
  status: 'in' | 'out' | 'absent' | 'late' | 'no_punch';
  total_hours?: number | string;
  is_late: boolean;
  late_minutes?: number;
  is_half_day?: boolean;
  location?: string;
  notes?: string;
  breaks?: Array<{
    id: string;
    date?: string;
    start_time?: string;
    end_time?: string;
    duration_minutes?: number;
    extra_minutes?: number;
    status?: string;
  }>;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    employee_id: string;
    profile_image_url?: string | null;
    store?: {
      id: string;
      name: string;
    };
    cluster?: {
      id: string;
      name: string;
    };
    shift?: {
      id: string;
      name: string;
      start_time?: string;
      end_time?: string;
    };
  };
  shift?: {
    id: string;
    name: string;
    start_time?: string;
    end_time?: string;
  };
}

export interface AttendanceStats {
  totalDays: number;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  totalHours: number;
  averageHours: number;
  attendancePercentage: number;
}

export interface AttendanceFilters {
  userId?: string;
  storeId?: string;
  clusterId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  role?: string;
  page?: number;
  limit?: number;
  // Export only: 'inactive' = deactivated employees who punched in during the range
  employeeStatus?: 'active' | 'inactive';
}

// Headline counts across every matching employee (not just the loaded page)
export interface AttendanceSummary {
  total_employees: number;
  present: number;
  absent: number;
  late: number;
  week_off: number;
  checked_out: number;
  avg_hours: number;
}

const attendanceService = {
  /**
   * Get attendance records with filters
   */
  getAttendanceRecords: async (filters: AttendanceFilters = {}): Promise<{
    records: AttendanceRecord[];
    summary?: AttendanceSummary;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    // Convert camelCase to snake_case for backend compatibility
    const backendFilters = {
      ...filters,
      start_date: filters.startDate,
      end_date: filters.endDate,
      user_id: filters.userId,
      store_id: filters.storeId,
      cluster_id: filters.clusterId,
    };
    
    // Remove camelCase properties
    delete backendFilters.startDate;
    delete backendFilters.endDate;
    delete backendFilters.userId;
    delete backendFilters.storeId;
    delete backendFilters.clusterId;
    
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        records: AttendanceRecord[];
        summary?: AttendanceSummary;
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      };
    }>(API_ENDPOINTS.ATTENDANCE.LIST, {
      params: backendFilters,
    });
    return response.data.data;
  },

  /**
   * Get attendance records for a specific user
   */
  getUserAttendance: async (userId: string, filters: AttendanceFilters = {}): Promise<{
    records: AttendanceRecord[];
    stats: AttendanceStats;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: {
        records: AttendanceRecord[];
        stats: AttendanceStats;
      };
    }>(API_ENDPOINTS.ATTENDANCE.USER_ATTENDANCE(userId), {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get attendance statistics
   */
  getAttendanceStats: async (filters: AttendanceFilters = {}): Promise<AttendanceStats> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: AttendanceStats;
    }>(API_ENDPOINTS.ATTENDANCE.STATS, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get attendance analytics
   */
  getAttendanceAnalytics: async (filters: AttendanceFilters = {}): Promise<{
    dailyStats: Array<{
      date: string;
      present: number;
      absent: number;
      late: number;
      totalHours: number;
    }>;
    weeklyStats: Array<{
      week: string;
      present: number;
      absent: number;
      late: number;
      averageHours: number;
    }>;
    monthlyStats: Array<{
      month: string;
      present: number;
      absent: number;
      late: number;
      averageHours: number;
    }>;
    topPerformers: Array<{
      user: {
        id: string;
        first_name: string;
        last_name: string;
        employee_id: string;
      };
      attendancePercentage: number;
      totalHours: number;
    }>;
  }> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: any;
    }>(API_ENDPOINTS.ATTENDANCE.ANALYTICS, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Create attendance record
   */
  createAttendanceRecord: async (data: {
    user_id: string;
    date: string;
    clock_in_time?: string;
    clock_out_time?: string;
    status: string;
    location?: string;
    notes?: string;
  }): Promise<AttendanceRecord> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: AttendanceRecord;
    }>(API_ENDPOINTS.ATTENDANCE.CREATE, data);
    return response.data.data;
  },

  /**
   * Update attendance record
   */
  updateAttendanceRecord: async (id: string, data: Partial<AttendanceRecord>): Promise<AttendanceRecord> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: AttendanceRecord;
    }>(API_ENDPOINTS.ATTENDANCE.UPDATE(id), data);
    return response.data.data;
  },

  /**
   * Delete attendance record
   */
  deleteAttendanceRecord: async (id: string): Promise<void> => {
    await api.delete(API_ENDPOINTS.ATTENDANCE.DELETE(id));
  },

  /**
   * Export attendance data
   */
  exportAttendanceData: async (filters: AttendanceFilters = {}): Promise<Blob> => {
    const response = await api.get(API_ENDPOINTS.ATTENDANCE.EXPORT, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },
};

export default attendanceService;
